import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Check, X, LogIn, LogOut, RefreshCw, ZapOff, WifiOff, DoorOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Toaster, toast } from "sonner";
import { Panel, Eyebrow, MonoLabel, LiveDot } from "@/components/Panel";
import { scannerAuth, scannerApi, offlineQueue } from "@/lib/scannerApi";
import { apiErr } from "@/lib/authApi";

const VIEWPORT = "qr-camera-viewport";
const DEBOUNCE_MS = 2000;
const REJECT_REASONS = {
  invalid_qr: "Invalid QR — signature check failed",
  wrong_event: "Wrong event · pass belongs elsewhere",
  payment_pending: "Payment pending · not entitled",
  cancelled: "Registration cancelled · not entitled",
  tier_not_allowed: "Tier not entitled to this meal",
  outside_window: "Outside the meal window",
  already_checked_in: "Already checked in for this meal",
  entry_required: "No reception entry yet — check in at the entry desk first",
};

function beep(ok) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.frequency.value = ok ? 900 : 240; g.gain.value = 0.06;
    o.connect(g); g.connect(ctx.destination); o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, ok ? 120 : 260);
  } catch {}
}

export default function Scanner() {
  const [authed, setAuthed] = useState(!!scannerAuth.token);
  const [exchanging, setExchanging] = useState(() => !scannerAuth.token && !!(localStorage.getItem("bni-admin-token") || localStorage.getItem("bni-devadmin-token")));

  useEffect(() => {
    if (authed) return;
    const adminTok = localStorage.getItem("bni-admin-token") || localStorage.getItem("bni-devadmin-token");
    if (!adminTok) { setExchanging(false); return; }
    let cancelled = false;
    scannerAuth.exchangeFromAdmin()
      .then(() => { if (!cancelled) setAuthed(true); })
      .catch(() => { if (!cancelled) setExchanging(false); })
      .finally(() => { if (!cancelled) setExchanging(false); });
    return () => { cancelled = true; };
  }, [authed]);

  if (!authed && exchanging) return <div className="min-h-screen flex items-center justify-center mono-label" style={{ color: "var(--taupe)" }}>Signing you in…</div>;
  if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />;
  return <Console onLogout={() => { scannerAuth.clear(); setAuthed(false); }} />;
}

function Console({ onLogout }) {
  const [windows, setWindows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [camError, setCamError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(offlineQueue.size());

  const scannerRef = useRef(null);
  const lastTokenRef = useRef({ token: "", ts: 0 });

  const loadWindows = async () => {
    try {
      const r = await scannerApi.mealWindows();
      setWindows(r.items || []);
      if (r.items?.length && !selectedId) {
        const active = r.items.find((m) => m.status === "active") || r.items[0];
        setSelectedId(active.id);
      }
    } catch (e) { toast.error(apiErr(e)); }
  };
  const loadRecent = async () => { try { const r = await scannerApi.recent(20); setRecent(r.items || []); } catch {} };

  useEffect(() => { loadWindows(); loadRecent(); }, []); // eslint-disable-line
  useEffect(() => {
    const iv = setInterval(loadWindows, 60_000); // refresh statuses each minute
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const on = () => { setOnline(true); drain(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []); // eslint-disable-line

  const drain = async () => {
    if (!navigator.onLine) return;
    const r = await offlineQueue.drain((res) => {
      setResult({ ...res, mealName: windows.find((m) => m.id === res.meal_window_id)?.name || "", ts: new Date() });
    });
    if (r.processed) toast.success(`Synced ${r.processed} queued scan${r.processed === 1 ? "" : "s"}`);
    setPending(offlineQueue.size());
    if (r.processed) loadRecent();
  };
  useEffect(() => { if (online) drain(); }, [online]); // eslint-disable-line

  const stopCamera = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const onDecoded = async (token) => {
    const now = Date.now();
    if (token === lastTokenRef.current.token && now - lastTokenRef.current.ts < DEBOUNCE_MS) return;
    lastTokenRef.current = { token, ts: now };
    const meal = windows.find((m) => m.id === selectedId);
    if (!meal) return;
    const body = { qr_token: token, meal_window_id: selectedId };
    if (!navigator.onLine) {
      const added = offlineQueue.enqueue(body);
      setPending(offlineQueue.size());
      if (added) toast.info("Offline · queued for sync"); else toast("Duplicate — already queued");
      return;
    }
    try {
      const r = await scannerApi.checkin(body);
      const granted = r.status === "granted";
      setFlash(granted ? "green" : "red");
      beep(granted);
      setResult({ ...r, mealName: meal.name, ts: new Date() });
      setTimeout(() => setFlash(null), 900);
      // optimistic prepend
      setRecent((prev) => [{
        id: r.id, status: r.status, reject_reason: r.reason === "granted" ? null : r.reason,
        scanned_at: new Date().toISOString(), meal_window_id: selectedId,
        registration: r.member ? { name: r.member.name, photo_url: r.member.photo_url, chapter: r.member.chapter } : null,
      }, ...prev].slice(0, 20));
    } catch (e) {
      if (!navigator.onLine || e?.message?.includes("Network")) {
        offlineQueue.enqueue(body); setPending(offlineQueue.size());
        toast.info("Network error — queued");
      } else toast.error(apiErr(e));
    }
  };

  const startCamera = useCallback(async () => {
    if (!selectedId) { toast.error("Pick a meal window first"); return; }
    setCamError(null);
    try {
      const q = new Html5Qrcode(VIEWPORT, { verbose: false });
      scannerRef.current = q;
      await q.start({ facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
        (decoded) => onDecoded(decoded), () => {});
      setScanning(true);
    } catch (e) {
      const msg = e?.message || String(e);
      setCamError(msg); toast.error(`Camera unavailable: ${msg}`);
    }
  }, [selectedId, windows]); // eslint-disable-line

  useEffect(() => () => { stopCamera(); }, [stopCamera]);
  const selectedWindow = windows.find((m) => m.id === selectedId);

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Eyebrow>Scanner · Meal & Access</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">Point. Scan. Serve.</h1>
            <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
              Signed in as <span style={{ color: "var(--text)" }}>{scannerAuth.userName}</span> · access refreshes every hour
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/reception" className="btn-ghost text-xs" data-testid="scanner-reception-link"><DoorOpen size={12} /> Reception desk</Link>
            {!online && <span className="chip" data-testid="scanner-offline-chip" style={{ color: "var(--red-2)", borderColor: "var(--red-2)" }}><WifiOff size={12} /> OFFLINE</span>}
            {pending > 0 && <span className="chip" data-testid="scanner-pending-chip" style={{ color: "var(--taupe)" }}>{pending} pending sync</span>}
            <button data-testid="scanner-logout-btn" onClick={() => { stopCamera(); onLogout(); }} className="btn-ghost text-xs"><LogOut size={12} /> Close of day</button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <Panel className="p-0 overflow-hidden">
              <div className="p-5 border-b border-[var(--hairline)] flex items-center justify-between flex-wrap gap-3">
                <MonoLabel>Check-in mode</MonoLabel>
                <div className="flex flex-wrap gap-2">
                  {windows.map((m) => {
                    const locked = m.status !== "active";
                    return (
                      <button key={m.id} data-testid={`meal-${m.id}`} onClick={() => !locked && setSelectedId(m.id)}
                        disabled={locked} title={locked ? `${m.status}` : "select"}
                        className={`chip ${selectedId === m.id ? "red" : ""}`}
                        style={{ cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.4 : 1 }}>
                        {m.name} · {m.start}–{m.end} · <span className="ml-1 mono-label">{m.status}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="relative bg-black" style={{ aspectRatio: "3/4" }}>
                <div id={VIEWPORT} className="absolute inset-0" />
                {!scanning && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6"
                       style={{ background: "linear-gradient(180deg,#111,#050505)" }}>
                    <Camera size={36} style={{ color: "var(--taupe)" }} />
                    <div className="mono-label" style={{ color: "var(--taupe)" }}>{camError ? "Camera blocked" : "Camera stopped"}</div>
                    {camError && <div className="text-xs max-w-[420px]" style={{ color: "var(--body)" }}>{camError}</div>}
                    <button data-testid="scanner-start-btn" onClick={startCamera} className="btn-red"><Camera size={14} /> Start camera</button>
                  </div>
                )}
                <AnimatePresence>
                  {flash === "green" && <motion.div key="g" className="scan-flash-green" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} />}
                  {flash === "red" && <motion.div key="r" className="scan-flash-red" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} />}
                </AnimatePresence>
                <AnimatePresence>
                  {result && (
                    <motion.div key={result.ts.getTime()} initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{opacity:0}}
                      className="absolute left-4 right-4 bottom-16 hairline p-4 pointer-events-none"
                      data-testid={`scan-result-${result.status === "granted" ? "ok" : "fail"}`}
                      style={{ background: result.status === "granted" ? "rgba(63,191,127,0.14)" : "rgba(204,37,47,0.14)",
                               borderColor: result.status === "granted" ? "var(--green)" : "var(--red)" }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center" style={{ background: result.status === "granted" ? "var(--green)" : "var(--red)" }}>
                          {result.status === "granted" ? <Check size={20} /> : <X size={20} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{result.member?.name || (REJECT_REASONS[result.reason] || result.reason)}</div>
                          <div className="mono-label truncate">{result.member?.chapter || result.mealName}</div>
                        </div>
                        <div className="chip" style={{ color: result.status === "granted" ? "var(--green)" : "var(--red)",
                                                        borderColor: result.status === "granted" ? "var(--green)" : "var(--red)" }}>
                          {result.status.toUpperCase()}
                        </div>
                      </div>
                      {result.status !== "granted" && <div className="mt-3 text-xs" style={{ color: "var(--body)" }}>{REJECT_REASONS[result.reason] || result.reason}</div>}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="p-5 border-t border-[var(--hairline)] flex items-center gap-3 flex-wrap">
                {scanning
                  ? <button data-testid="scanner-stop-btn" onClick={stopCamera} className="btn-ghost"><ZapOff size={14} /> Stop</button>
                  : <button data-testid="scanner-start-inline-btn" onClick={startCamera} className="btn-red"><Camera size={14} /> Start</button>}
                <button data-testid="scanner-refresh-btn" onClick={() => { loadWindows(); loadRecent(); }} className="btn-ghost"><RefreshCw size={14} /> Refresh</button>
                {pending > 0 && <button data-testid="scanner-sync-btn" onClick={drain} className="btn-ghost">Sync {pending} pending</button>}
                <span className="mono-label ml-auto" style={{ color: "var(--taupe)" }}>{selectedWindow ? `${selectedWindow.name} · ${selectedWindow.status}` : "no window selected"}</span>
              </div>
            </Panel>
          </div>

          <Panel className="lg:col-span-5 p-6">
            <div className="flex items-center justify-between">
              <MonoLabel>My recent scans</MonoLabel>
              <LiveDot />
            </div>
            <div className="mt-3 max-h-[520px] overflow-auto pr-1 space-y-2" data-testid="scanner-recent">
              {recent.length === 0 && <div className="mono-label" style={{ color: "var(--taupe)" }}>No scans yet — start the camera</div>}
              {recent.map((r) => (
                <div key={r.id} className="flex items-center gap-3 hairline p-2.5" data-testid={`recent-${r.id.slice(0,8)}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: r.status === "granted" ? "var(--green)" : "var(--red)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs truncate">{r.registration?.name || (REJECT_REASONS[r.reject_reason] || r.reject_reason || "—")}</div>
                    <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>
                      {r.status === "granted" ? (r.registration?.chapter || "granted") : REJECT_REASONS[r.reject_reason] || r.reject_reason}
                    </div>
                  </div>
                  <div className="mono-label" style={{ color: "var(--muted)" }}>{fmt(new Date(r.scanned_at))}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export function LoginGate({ onSuccess }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e?.preventDefault?.(); setErr(""); setBusy(true);
    try { await scannerAuth.login(phone.replace(/\D/g, "").slice(-10), password); toast.success("Signed in · scanner"); onSuccess(); }
    catch (ex) { setErr(apiErr(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[440px] mx-auto px-4 sm:px-8 py-16">
        <div className="flex justify-center mb-6" data-testid="scanner-login-logo-wrap">
          <img src="/risse-bni-dark.webp" alt="RISSE BNI 26 · Davanagere Members Day" className="h-28 w-auto" data-testid="scanner-login-logo" />
        </div>
        <Eyebrow>Scanner Access</Eyebrow>
        <h1 className="h-hero mt-3 text-3xl">Sign in — 1-hour access</h1>
        <Panel className="p-6 mt-6">
          <form onSubmit={submit}>
            <MonoLabel>Phone</MonoLabel>
            <input data-testid="scanner-login-phone" className="input-x mt-3" placeholder="10-digit" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <MonoLabel className="mt-4 block">Password</MonoLabel>
            <input data-testid="scanner-login-password" type="password" className="input-x mt-3" value={password} onChange={(e) => setPassword(e.target.value)} />
            {err && <div data-testid="scanner-login-err" className="text-sm mt-3" style={{ color: "var(--red-2)" }}>{err}</div>}
            <button data-testid="scanner-login-btn" type="submit" disabled={busy} className="btn-red mt-5 w-full">
              <LogIn size={14} /> {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mono-label mt-4" style={{ color: "var(--muted)" }}>Admin or DevAdmin credentials · scoped to this event only</div>
        </Panel>
      </div>
    </div>
  );
}

function fmt(d) {
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return d.toLocaleTimeString();
}
